/**
 * Domain-specific state management utilities for operation locking and optimistic updates
 * These utilities are retained as they provide specialized functionality not available in standard libraries
 */

import React, { useCallback, useRef } from 'react';

/**
 * Lock manager to prevent concurrent operations
 */
export class OperationLock {
  private locks: Map<string, Promise<void>> = new Map();

  /**
   * Acquire a lock for an operation
   * @param key - Unique key for the operation
   * @param operation - Async operation to perform
   * @returns Result of the operation
   */
  async acquire<T>(key: string, operation: () => Promise<T>): Promise<T> {
    // Wait for any existing lock
    const existingLock = this.locks.get(key);
    if (existingLock) {
      await existingLock;
    }

    // Create new lock
    let releaseLock: () => void;
    const lockPromise = new Promise<void>((resolve) => {
      releaseLock = resolve;
    });
    this.locks.set(key, lockPromise);

    try {
      // Perform operation
      const result = await operation();
      return result;
    } finally {
      // Release lock
      this.locks.delete(key);
      releaseLock!();
    }
  }

  /**
   * Check if a lock exists for a key
   * @param key - Lock key to check
   * @returns true if locked
   */
  isLocked(key: string): boolean {
    return this.locks.has(key);
  }
}

/**
 * Hook for preventing concurrent operations
 * @returns Lock acquisition function
 */
export function useOperationLock() {
  const lockRef = useRef(new OperationLock());

  const acquireLock = useCallback(
    async <T>(key: string, operation: () => Promise<T>): Promise<T> => {
      return lockRef.current.acquire(key, operation);
    },
    []
  );

  const isLocked = useCallback((key: string): boolean => {
    return lockRef.current.isLocked(key);
  }, []);

  return { acquireLock, isLocked };
}

/**
 * Optimistic update manager
 */
export interface OptimisticUpdate<T> {
  id: string;
  oldValue: T;
  newValue: T;
  timestamp: number;
}

export class OptimisticUpdateManager<T> {
  private updates: Map<string, OptimisticUpdate<T>> = new Map();
  private rollbackTimeout = 30000; // 30 seconds

  /**
   * Apply an optimistic update
   * @param id - Unique identifier for the update
   * @param oldValue - Previous value
   * @param newValue - New value to apply
   * @returns Update object
   */
  apply(id: string, oldValue: T, newValue: T): OptimisticUpdate<T> {
    const update: OptimisticUpdate<T> = {
      id,
      oldValue,
      newValue,
      timestamp: Date.now(),
    };
    
    this.updates.set(id, update);
    
    // Auto-cleanup old updates
    setTimeout(() => {
      this.updates.delete(id);
    }, this.rollbackTimeout);
    
    return update;
  }

  /**
   * Confirm an optimistic update
   * @param id - Update identifier
   */
  confirm(id: string): void {
    this.updates.delete(id);
  }

  /**
   * Rollback an optimistic update
   * @param id - Update identifier
   * @returns Previous value if found
   */
  rollback(id: string): T | undefined {
    const update = this.updates.get(id);
    if (update) {
      this.updates.delete(id);
      return update.oldValue;
    }
    return undefined;
  }

  /**
   * Get all pending updates
   * @returns Map of pending updates
   */
  getPendingUpdates(): Map<string, OptimisticUpdate<T>> {
    return new Map(this.updates);
  }

  /**
   * Clear all updates
   */
  clear(): void {
    this.updates.clear();
  }
}

/**
 * Hook for optimistic updates
 * @returns Optimistic update functions
 */
export function useOptimisticUpdate<T>() {
  const managerRef = useRef(new OptimisticUpdateManager<T>());

  const applyUpdate = useCallback(
    (id: string, oldValue: T, newValue: T): OptimisticUpdate<T> => {
      return managerRef.current.apply(id, oldValue, newValue);
    },
    []
  );

  const confirmUpdate = useCallback((id: string): void => {
    managerRef.current.confirm(id);
  }, []);

  const rollbackUpdate = useCallback((id: string): T | undefined => {
    return managerRef.current.rollback(id);
  }, []);

  return { applyUpdate, confirmUpdate, rollbackUpdate };
}

/**
 * State version manager to prevent stale updates
 */
export class StateVersionManager {
  private versions: Map<string, number> = new Map();

  /**
   * Get current version for a key
   * @param key - State key
   * @returns Current version number
   */
  getVersion(key: string): number {
    return this.versions.get(key) || 0;
  }

  /**
   * Increment version for a key
   * @param key - State key
   * @returns New version number
   */
  incrementVersion(key: string): number {
    const currentVersion = this.getVersion(key);
    const newVersion = currentVersion + 1;
    this.versions.set(key, newVersion);
    return newVersion;
  }

  /**
   * Check if a version is current
   * @param key - State key
   * @param version - Version to check
   * @returns true if version is current
   */
  isCurrent(key: string, version: number): boolean {
    return this.getVersion(key) === version;
  }

  /**
   * Reset version for a key
   * @param key - State key
   */
  reset(key: string): void {
    this.versions.delete(key);
  }
}

/**
 * Hook for managing state versions
 * @param key - State key
 * @returns Version management functions
 */
export function useStateVersion(key: string) {
  const managerRef = useRef(new StateVersionManager());
  const [version, setVersion] = React.useState(0);

  const incrementVersion = useCallback(() => {
    const newVersion = managerRef.current.incrementVersion(key);
    setVersion(newVersion);
    return newVersion;
  }, [key]);

  const checkVersion = useCallback(
    (versionToCheck: number): boolean => {
      return managerRef.current.isCurrent(key, versionToCheck);
    },
    [key]
  );

  const resetVersion = useCallback(() => {
    managerRef.current.reset(key);
    setVersion(0);
  }, [key]);

  return { version, incrementVersion, checkVersion, resetVersion };
}